import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActionItemComponent } from '../../shared/action-item.component';
import { NgbActiveModal, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { MarksheetDto, PostDto } from '@oracychampions/api-interfaces';
import { criteria, CurriculumArea, CurriculumYear } from './criteria';
import { NoItemsComponent } from '../../shared/no-items.component';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { ToastrService } from 'ngx-toastr';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { tablerCheck } from '@ng-icons/tabler-icons';
import { AudioPlayerComponent } from '../../shared/audio-player';

@Component({
  selector: 'oc-marksheet',
  standalone: true,
  imports: [
    CommonModule,
    ActionItemComponent,
    NoItemsComponent,
    NgbNavModule,
    FormsModule,
    NgIconComponent,
    AudioPlayerComponent,
  ],
  providers: [
    provideIcons({
      tablerCheck,
    }),
  ],
  template: `
    <div class="modal-container">
      <div class="modal-body">
        <div class="modal-heading">
          <div class="d-flex gap-3 align-items-center">
            <h1>Marksheet</h1>
            <oc-audio-player [src]="post.audioURL"></oc-audio-player>
          </div>
          <div *ngIf="!readonly" class="d-flex gap-2">
            <a class="btn btn-secondary" (click)="activeModal.dismiss('Cancel click')">Cancel</a>
            <a class="btn btn-primary" (click)="saveChanges()">Save Changes</a>
          </div>
          <oc-action-item
            *ngIf="readonly"
            icon="tablerX"
            size="lg"
            (click)="activeModal.dismiss('Cross click')"
          ></oc-action-item>
        </div>
        <div *ngIf="(post.students?.length ?? 0) === 0">
          <oc-no-items
            mainTitle="No Marksheets"
            subTitle="There are no students associated with this post"
          ></oc-no-items>
        </div>
        <div *ngIf="(post.students?.length ?? 0) > 0">
          <ul ngbNav #nav="ngbNav" class="nav-tabs" [style.border-width]="studentTabs ? 1 : 0">
            <li ngbNavItem *ngFor="let student of post.students">
              <button *ngIf="studentTabs" ngbNavLink>{{ student.name }}</button>
              <ng-template ngbNavContent
                ><div class="oc-marksheet-marksheet">
                  <div>
                    <ul ngbNav #nav2="ngbNav" class="nav-pills">
                      <li ngbNavItem *ngFor="let area of areas">
                        <button ngbNavLink>{{ area.title }}</button>
                        <ng-template ngbNavContent
                          ><div class="oc-marksheet-filtered">
                            <table class="table table-bordered">
                              <thead>
                                <tr>
                                  <th>&nbsp;</th>
                                  <th *ngIf="area.area === null">&nbsp;</th>
                                  <th>&nbsp;</th>
                                  <th class="grade">
                                    <div><span>Emerging</span></div>
                                  </th>
                                  <th class="grade">
                                    <div><span>Developing</span></div>
                                  </th>
                                  <th class="grade">
                                    <div><span>Secure</span></div>
                                  </th>
                                  <th class="grade">
                                    <div><span>Mastered</span></div>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr *ngFor="let item of filteredCriteria(area.area)">
                                  <td>{{ item.description }}</td>
                                  <td *ngIf="area.area === null" class="pinch">{{ areaTitle(item.area) }}</td>
                                  <td class="pinch">{{ yearTitle(item.year) }}</td>
                                  <td class="grade">
                                    <label for="{{ item.key + '-0' }}"></label>
                                    <input
                                      *ngIf="!readonly"
                                      class="form-check-input"
                                      type="radio"
                                      [(ngModel)]="model[student.id][item.key]"
                                      [ngModelOptions]="{ standalone: true }"
                                      id="{{ student.id + '-' + item.key + '-0' }}"
                                      name="{{ student.id + '-' + item.key }}"
                                      value="0"
                                      (click)="click($event, model[student.id], item.key)"
                                    />
                                    <ng-icon
                                      *ngIf="readonly && model[student.id][item.key] === '0'"
                                      name="tablerCheck"
                                      size="20"
                                      color="#462664"
                                      style="position: relative; top: 6px;"
                                    ></ng-icon>
                                  </td>
                                  <td class="grade">
                                    <label for="{{ item.key + '-1' }}"></label>
                                    <input
                                      *ngIf="!readonly"
                                      class="form-check-input"
                                      type="radio"
                                      [(ngModel)]="model[student.id][item.key]"
                                      [ngModelOptions]="{ standalone: true }"
                                      id="{{ student.id + '-' + item.key + '-1' }}"
                                      name="{{ student.id + '-' + item.key }}"
                                      value="1"
                                      (click)="click($event, model[student.id], item.key)"
                                    />
                                    <ng-icon
                                      *ngIf="readonly && model[student.id][item.key] === '1'"
                                      name="tablerCheck"
                                      size="20"
                                      color="#462664"
                                      style="position: relative; top: 6px;"
                                    ></ng-icon>
                                  </td>
                                  <td class="grade">
                                    <label for="{{ item.key + '-2' }}"></label>
                                    <input
                                      *ngIf="!readonly"
                                      class="form-check-input"
                                      type="radio"
                                      [(ngModel)]="model[student.id][item.key]"
                                      [ngModelOptions]="{ standalone: true }"
                                      id="{{ student.id + '-' + item.key + '-2' }}"
                                      name="{{ student.id + '-' + item.key }}"
                                      value="2"
                                      (click)="click($event, model[student.id], item.key)"
                                    />
                                    <ng-icon
                                      *ngIf="readonly && model[student.id][item.key] === '2'"
                                      name="tablerCheck"
                                      size="20"
                                      color="#462664"
                                      style="position: relative; top: 6px;"
                                    ></ng-icon>
                                  </td>
                                  <td class="grade">
                                    <label for="{{ item.key + '-3' }}"></label>
                                    <input
                                      *ngIf="!readonly"
                                      class="form-check-input"
                                      type="radio"
                                      [(ngModel)]="model[student.id][item.key]"
                                      [ngModelOptions]="{ standalone: true }"
                                      id="{{ student.id + '-' + item.key + '-3' }}"
                                      name="{{ student.id + '-' + item.key }}"
                                      value="3"
                                      (click)="click($event, model[student.id], item.key)"
                                    />
                                    <ng-icon
                                      *ngIf="readonly && model[student.id][item.key] === '3'"
                                      name="tablerCheck"
                                      size="20"
                                      color="#462664"
                                      style="position: relative; top: 6px;"
                                    ></ng-icon>
                                  </td>
                                </tr>
                              </tbody>
                            </table></div
                        ></ng-template>
                      </li>
                    </ul>
                    <div [ngbNavOutlet]="nav2"></div>
                  </div></div
              ></ng-template>
            </li>
          </ul>
          <div [ngbNavOutlet]="nav"></div>
        </div>
      </div>
    </div>
  `,
  styles: `
    .modal-container {
      display: flex;
      min-height: 100vh;
    }

    .modal-heading {
      display: flex;
      align-items: center;
      margin-bottom: 1.5rem;
      justify-content: space-between;
    }

    .modal-body {
      padding: 2rem 3rem;
    }

    h1 {
      margin: 0;
    }

    .oc-marksheet-marksheet {
      padding-block: 1rem;
    }

    .oc-marksheet-filtered {
      padding-block: 1rem;
    }

    .pinch {
      width: 0;
      white-space: nowrap;
    }

    .grade {
      width: 50px;
      max-width: 50px;
      white-space: nowrap;
      text-align: center;
      text-overflow: ellipsis;
      overflow-x: hidden;
      padding-inline: 5px;
      position: relative;

      label {
        position:absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
      }

      input {
        position: relative;
      }
    }
  `,
})
export class MarksheetComponent implements OnInit {
  public activeModal = inject(NgbActiveModal);
  public http = inject(HttpClient);
  private toastr = inject(ToastrService);

  public post: PostDto;
  public readonly = false;
  public studentTabs = true;

  public areas: { area: CurriculumArea | null; title: string }[] = [
    {
      area: null,
      title: 'All',
    },
    {
      area: CurriculumArea.Physical,
      title: 'Physical',
    },
    {
      area: CurriculumArea.Linguistic,
      title: 'Linguistic',
    },
    {
      area: CurriculumArea.Cognitive,
      title: 'Cognitive',
    },
    {
      area: CurriculumArea.SocialEmotional,
      title: 'Social and Emotional',
    },
  ];

  public criteria = criteria;
  public model: any = {};

  ngOnInit() {
    this.post.students?.forEach((student) => {
      this.model[student.id] = {};
    });
    this.http.get<MarksheetDto[]>(`${environment.API}/posts/${this.post.id}/marksheets`).subscribe((marksheets) => {
      for (const marksheet of marksheets) {
        Object.entries(marksheet.grades).forEach(([key, grade]) => {
          if (this.model[marksheet.studentId]) {
            this.model[marksheet.studentId][key] = `${grade}`;
          }
        });
      }
    });
  }

  filteredCriteria(area: CurriculumArea | null) {
    return this.criteria.filter((item) => item.area === area || area === null);
  }

  areaTitle(year: CurriculumArea) {
    return ['Physical', 'Linguistic', 'Cognitive', 'Social and Emotional'][year];
  }

  yearTitle(area: CurriculumYear) {
    return ['Reception', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6'][area];
  }

  click(event: Event, item: any, propertyName: string) {
    if (item !== undefined) {
      delete item[propertyName];
    }
  }

  saveChanges() {
    const postRequest$ = Object.entries(this.model).map(([key, grades]) => {
      const body = {
        postId: this.post.id,
        studentId: key,
        grades,
      };
      return this.http.put(`${environment.API}/marksheets`, body);
    });

    forkJoin(postRequest$).subscribe(() => {
      this.toastr.success(Object.values(this.model).length > 1 ? 'Marksheets updated' : 'Marksheet updated');
      this.activeModal.close();
    });
  }
}
